"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpDown,
  CheckCircle2,
  Clock,
  Filter,
  Plus,
  Search,
  Wrench,
  XCircle,
} from "lucide-react";
import { useState } from "react";

interface Request {
  id: number;
  title: string;
  description: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high";
  type: string;
  createdAt: Date;
  updatedAt: Date;
}

const mockRequests: Request[] = [
  {
    id: 1,
    title: "Ремонт сантехніки",
    description: "Протікає кран у ванній кімнаті",
    status: "pending",
    priority: "high",
    type: "repair",
    createdAt: new Date(2024, 2, 1),
    updatedAt: new Date(2024, 2, 1),
  },
  {
    id: 2,
    title: "Заміна лампочок",
    description: "Необхідно замінити лампочки в коридорі",
    status: "in_progress",
    priority: "low",
    type: "maintenance",
    createdAt: new Date(2024, 2, 2),
    updatedAt: new Date(2024, 2, 3),
  },
  {
    id: 3,
    title: "Прибирання території",
    description: "Прибирання прибудинкової території",
    status: "completed",
    priority: "medium",
    type: "cleaning",
    createdAt: new Date(2024, 2, 3),
    updatedAt: new Date(2024, 2, 4),
  },
];

const statusMap = {
  pending: { label: "Очікує", color: "bg-yellow-500" },
  in_progress: { label: "В роботі", color: "bg-blue-500" },
  completed: { label: "Виконано", color: "bg-green-500" },
  cancelled: { label: "Скасовано", color: "bg-red-500" },
};

const priorityMap = {
  low: { label: "Низький", color: "bg-gray-500" },
  medium: { label: "Середній", color: "bg-yellow-500" },
  high: { label: "Високий", color: "bg-red-500" },
};

const RequestsPage = () => {
  const [requests, setRequests] = useState<Request[]>(mockRequests);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"date" | "priority" | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

  const filteredRequests = requests
    .filter((request) => {
      const matchesSearch = request.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || request.status === statusFilter;
      const matchesPriority =
        priorityFilter === "all" || request.priority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    })
    .sort((a, b) => {
      if (!sortBy) return 0;
      if (sortBy === "date") {
        return sortOrder === "asc"
          ? a.createdAt.getTime() - b.createdAt.getTime()
          : b.createdAt.getTime() - a.createdAt.getTime();
      }
      if (sortBy === "priority") {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return sortOrder === "asc"
          ? priorityOrder[a.priority] - priorityOrder[b.priority]
          : priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return 0;
    });

  const handleSort = (field: "date" | "priority") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleStatusChange = async (
    requestId: number,
    newStatus: Request["status"],
  ) => {
    setRequests((prev) =>
      prev.map((request) =>
        request.id === requestId
          ? { ...request, status: newStatus, updatedAt: new Date() }
          : request,
      ),
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container mx-auto p-8"
    >
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <Wrench className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Управління заявками</h1>
            <p className="text-muted-foreground">
              Перегляд та управління заявками на ремонт
            </p>
          </div>
        </div>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Створити заявку
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid gap-6"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Пошук заявок..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всі статуси</SelectItem>
                {Object.entries(statusMap).map(([value, { label }]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Пріоритет" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всі пріоритети</SelectItem>
                {Object.entries(priorityMap).map(([value, { label }]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Сортувати за</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleSort("date")}>
                  <Clock className="mr-2 h-4 w-4" />
                  Датою {sortBy === "date" && (sortOrder === "asc" ? "↑" : "↓")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSort("priority")}>
                  <Filter className="mr-2 h-4 w-4" />
                  Пріоритетом{" "}
                  {sortBy === "priority" && (sortOrder === "asc" ? "↑" : "↓")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <ScrollArea className="h-[600px] rounded-md border">
          <div className="p-4">
            <AnimatePresence>
              {filteredRequests.map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="mb-4">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>{request.title}</CardTitle>
                          <CardDescription>
                            Створено:{" "}
                            {format(request.createdAt, "d MMMM yyyy", {
                              locale: enUS,
                            })}
                          </CardDescription>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              Змінити статус
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {Object.entries(statusMap).map(
                              ([status, { label }]) => (
                                <DropdownMenuItem
                                  key={status}
                                  onClick={() =>
                                    handleStatusChange(
                                      request.id,
                                      status as Request["status"],
                                    )
                                  }
                                >
                                  {status === "completed" ? (
                                    <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                                  ) : status === "cancelled" ? (
                                    <XCircle className="mr-2 h-4 w-4 text-red-500" />
                                  ) : (
                                    <Clock className="mr-2 h-4 w-4" />
                                  )}
                                  {label}
                                </DropdownMenuItem>
                              ),
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4">
                        <p className="text-sm text-muted-foreground">
                          {request.description}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="secondary"
                            className={`${statusMap[request.status].color} text-white`}
                          >
                            {statusMap[request.status].label}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className={`${priorityMap[request.priority].color} text-white`}
                          >
                            {priorityMap[request.priority].label}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </ScrollArea>
      </motion.div>
    </motion.div>
  );
};

export default RequestsPage;
